import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Stack,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  VideoCall as VideoIcon,
  PlayArrow as StartIcon,
  Stop as StopIcon,
  Person as PersonIcon,
  Pets as PetsIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { api } from '@services/authService';

interface TelemedSession {
  id: string;
  scheduled_at: string;
  status: string;
  room_code: string;
  patient?: any;
  owner?: any;
  duration_seconds?: number;
}

const DoctorTelemed: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [sessions, setSessions] = useState<TelemedSession[]>([]);
  const [activeSession, setActiveSession] = useState<TelemedSession | null>(null);
  const [endDialogOpen, setEndDialogOpen] = useState(false);
  const [sessionNotes, setSessionNotes] = useState('');

  useEffect(() => {
    loadSessions();
  }, []);

  const loadSessions = async () => {
    try {
      setLoading(true);
      const response = await api.get('/doctor/telemed/sessions?status=scheduled,waiting,in_progress');
      setSessions(response.data.data);
    } catch (error) {
      console.error('Eroare la incarcarea sesiunilor:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStartSession = async (sessionId: string) => {
    try {
      const response = await api.post(`/doctor/telemed/sessions/${sessionId}/start`);
      setActiveSession(response.data.data);
      loadSessions();
    } catch (error) {
      console.error('Eroare la pornirea sesiunii:', error);
      alert('Eroare la pornirea sesiunii');
    }
  };

  const handleEndSession = async () => {
    if (!activeSession) return;

    try {
      await api.post(`/doctor/telemed/sessions/${activeSession.id}/end`, {
        notes: sessionNotes
      });

      alert('Sesiune finalizata cu succes!');
      setActiveSession(null);
      setEndDialogOpen(false);
      setSessionNotes('');
      loadSessions();
    } catch (error) {
      console.error('Eroare la finalizarea sesiunii:', error);
      alert('Eroare la finalizarea sesiunii');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'scheduled': return 'info';
      case 'waiting': return 'warning';
      case 'in_progress': return 'success';
      case 'completed': return 'default';
      default: return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    const labels: any = {
      scheduled: 'Programat',
      waiting: 'In asteptare',
      in_progress: 'In curs',
      completed: 'Finalizat',
      cancelled: 'Anulat'
    };
    return labels[status] || status;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Typography variant="h4" gutterBottom>
        Telemedicina
      </Typography>

      {/* Active Session */}
      {activeSession && (
        <Card sx={{ mb: 3, bgcolor: 'success.light' }}>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box display="flex" alignItems="center" gap={2}>
                <Avatar sx={{ bgcolor: 'success.main', width: 56, height: 56 }}>
                  <VideoIcon fontSize="large" />
                </Avatar>
                <Box>
                  <Typography variant="h6">Sesiune Activa</Typography>
                  <Typography variant="body2">
                    Cod camera: <strong>{activeSession.room_code}</strong>
                  </Typography>
                  <Typography variant="caption">
                    {activeSession.patient?.name || activeSession.owner?.firstName}
                  </Typography>
                </Box>
              </Box>
              <Button
                variant="contained"
                color="error"
                startIcon={<StopIcon />}
                onClick={() => setEndDialogOpen(true)}
              >
                Finalizeaza Sesiune
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Sessions List */}
      {sessions.length === 0 ? (
        <Alert severity="info">Nu exista sesiuni telemedicina programate</Alert>
      ) : (
        <Grid container spacing={2}>
          {sessions.map((session) => (
            <Grid size={{ xs: 12, md: 6 }} key={session.id}>
              <Card>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                    <Box display="flex" alignItems="center" gap={2}>
                      <Avatar sx={{ bgcolor: 'primary.main' }}>
                        {session.patient ? <PetsIcon /> : <PersonIcon />}
                      </Avatar>
                      <Box>
                        <Typography variant="h6">
                          {session.patient?.name || session.owner?.firstName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          <ScheduleIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                          {format(new Date(session.scheduled_at), 'dd MMM yyyy, HH:mm')}
                        </Typography>
                      </Box>
                    </Box>
                    <Chip
                      label={getStatusLabel(session.status)}
                      color={getStatusColor(session.status)}
                      size="small"
                    />
                  </Box>

                  <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
                    <Typography variant="body2" color="text.secondary">
                      Cod: <strong>{session.room_code}</strong>
                    </Typography>
                    {session.status !== 'in_progress' && (
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<StartIcon />}
                        onClick={() => handleStartSession(session.id)}
                      >
                        Porneste
                      </Button>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* End Session Dialog */}
      <Dialog open={endDialogOpen} onClose={() => setEndDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Finalizare Sesiune Telemedicina</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Sesiune: {activeSession?.room_code}
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={5}
            label="Note Consultatie"
            value={sessionNotes}
            onChange={(e) => setSessionNotes(e.target.value)}
            placeholder="Rezumat consultatie, recomandari, follow-up..."
            sx={{ mt: 2 }}
          />
          <Alert severity="info" sx={{ mt: 2 }}>
            Un raport automat va fi generat si atasat la dosarul pacientului.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEndDialogOpen(false)}>Anuleaza</Button>
          <Button variant="contained" onClick={handleEndSession}>
            Finalizeaza & Genereaza Raport
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DoctorTelemed;

